// client/src/pages/Guilds.jsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Shield, Plus, Users, Crown } from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { SkeletonList, Empty, Sheet, SectionHead } from '../components/ui';

export default function Guilds() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { guild, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', tag: '', description: '', is_open: true });

  const { data, isLoading } = useQuery({ queryKey: ['guilds'], queryFn: () => api.get('/guilds').then(r => r.data) });

  const createM = useMutation({
    mutationFn: () => api.post('/guilds', f),
    onSuccess: async ({ data }) => { toast.success('Guild founded! 🛡️'); await useAuth.getState().load(); nav(`/guild/${encodeURIComponent(data.handle)}`); },
    onError: e => toast.error(e.message),
  });

  const joinM = useMutation({
    mutationFn: (id) => api.post(`/guilds/${id}/join`),
    onSuccess: async () => { toast.success('Joined!'); await useAuth.getState().load(); qc.invalidateQueries(['guilds']); },
    onError: e => toast.error(e.message),
  });

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card p-5 bg-gradient-to-br from-grape-500 to-indigo-700 text-white">
        <Shield size={26} className="mb-2" />
        <h1 className="font-display text-2xl font-extrabold">Guilds</h1>
        <p className="text-white/85 text-sm mt-1">
          Team up. 10% of every member's shopping points feed the guild ranking.
        </p>
        {guild
          ? <Link to={`/guild/${encodeURIComponent(guild.handle)}`} className="btn bg-white text-grape-700 btn-sm mt-3 font-extrabold">
              My guild: {guild.name}</Link>
          : <button onClick={() => setOpen(true)} className="btn bg-white text-grape-700 btn-sm mt-3 font-extrabold">
              <Plus size={14} /> Found a guild · 1500💎</button>}
      </div>

      <SectionHead title="Top guilds" sub={`${data?.length || 0} active`} />
      {isLoading ? <SkeletonList n={6} h="h-18" />
        : !data?.length ? <Empty icon={Shield} title="No guilds yet" sub="Be the first to found one!" />
        : <div className="space-y-2">
          {data.map((g, i) => (
            <div key={g.id} className="card-h p-3 flex items-center gap-3">
              <Link to={`/guild/${encodeURIComponent(g.handle)}`} className="flex items-center gap-3 flex-1 min-w-0">
                <span className="w-6 text-center font-black text-xs text-ink-400">{i + 1}</span>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-grape-500 to-indigo-700 grid place-items-center text-white font-black text-xs shrink-0">
                  {g.emblem_url ? <img src={g.emblem_url} className="w-full h-full object-cover rounded-xl" alt="" /> : g.tag}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{g.name}</p>
                  <p className="text-[11px] text-ink-400 font-semibold flex items-center gap-1">
                    <Crown size={9} /> @{g.leader_username} · <Users size={9} /> {g.members_count}/{g.max_members}
                    {g.city_handle && ` · ${g.city_handle}`}
                  </p>
                </div>
              </Link>
              <div className="text-right shrink-0">
                <p className="font-black text-sm text-grape-600">{compact(g.guild_points)}</p>
                {!guild && g.is_open && g.members_count < g.max_members && (
                  <button onClick={() => joinM.mutate(g.id)} className="btn-soft btn-sm mt-1">Join</button>)}
              </div>
            </div>))}
        </div>}

      <Sheet open={open} onClose={() => setOpen(false)} title="Found a guild">
        <div className="space-y-3">
          <div><label className="label">Guild name</label>
            <input className="input" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Iron Dragons" /></div>
          <div><label className="label">Tag (2–6 chars)</label>
            <input className="input uppercase font-black tracking-widest" maxLength={6} value={f.tag}
              onChange={e => setF({ ...f, tag: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} placeholder="DRGN" /></div>
          <div><label className="label">Description</label>
            <textarea className="input min-h-[70px]" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm font-bold">
            <input type="checkbox" checked={f.is_open} onChange={e => setF({ ...f, is_open: e.target.checked })}
              className="w-4 h-4 accent-brand-500" /> Anyone can join
          </label>
          <div className="p-3 rounded-xl bg-brand-50 text-sm font-bold text-brand-700">
            Cost: 1500 points (you have {compact(user?.points_balance)})
          </div>
          <button onClick={() => createM.mutate()} disabled={createM.isPending || f.name.length < 3 || f.tag.length < 2}
            className="btn-primary w-full py-3">{createM.isPending ? 'Founding…' : 'Found guild'}</button>
        </div>
      </Sheet>
    </div>
  );
}